import React, { useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled/macro';
import { useSelector, useDispatch } from 'react-redux';
import { getCausesWithIndicators, getFlaggedCauseUuids } from '../../store/graph/graphSelectors';
import {
  getHighlightsBasedOnHoveredObservationId,
  getSelectedCause,
  getCorrectiveActionsForSelectedCause,
} from '../../store/ui/uiSelectors';
import { cardClick } from '../../store/ui/uiActions';
import {
  addDotCauses,
  markCauseSeen,
  clearExpiredDots,
  resetSeenCauses,
} from '../../store/causesTracking/causesTrackingActions';
import {
  getSeenCauses,
  getVisibleCausesCount,
} from '../../store/causesTracking/causesTrackingSelectors';

import MainList from '../List';
import ListHead from '../../components/List/ListHead';
import SelectedCause from './SelectedCause';
import { motion, AnimateSharedLayout } from 'framer-motion';
import _, { orderBy } from 'lodash';
import Card from '../../components/cards/Card';
import { Counteraction } from './Counteraction';
import {
  fetchSelectedCauseRankHistory,
  postCauseOrConsequenceClickedMetrics,
  postSessionClosedMetrics,
} from '../../store/api';
import { receiveRankingHistory } from '../../store/rankingHistory/rankingHistoryActions';
import { ReactComponent as IconTopBarLogo } from '../../svgs/topbar-logo.svg';
import HelpScreenLabel from '../../components/common/HelpScreenLabel';

const ConsequencesContainer = styled.div`
  display: grid;
  grid-template-rows: max-content 1fr;
  height: 100%;
  overflow: hidden;
`;

const StyledCausesListContainer = styled(motion.div)`
  display: grid;
  grid-template-rows: ${(props) =>
    props.withCounteractions
      ? 'max-content max-content max-content 1fr'
      : 'max-content max-content 1fr'};
  height: 100%;
  overflow: hidden;
`;

const StyledSelectedCausesLOL = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-20) var(--spacing-30);
`;

const StyledHeading = styled.h3`
  color: var(--color-gray-light);
  font-size: var(--font-size-14);
  text-transform: uppercase;
  font-weight: normal;
  margin: 0;
`;

const StyledClearButton = styled.button`
  color: var(--color-observations);
  font-size: var(--font-size-16);
`;

const Causes = () => {
  const causes = useSelector(getCausesWithIndicators);
  const { COUNTERACTIONS } = useSelector((state) => state.api.flags);
  const sessionId = useSelector((state) => state.api.sessionId);
  const helpPageOpen = useSelector((state) => state.ui.uiHelpPageOpen);
  const flaggedCauseUuids = useSelector(getFlaggedCauseUuids);
  const dispatch = useDispatch();
  const { highlightedCauses } = useSelector(getHighlightsBasedOnHoveredObservationId);

  const selected = useSelector(getSelectedCause);
  const corrective = useSelector(getCorrectiveActionsForSelectedCause);
  const seenCauses = useSelector(getSeenCauses);
  const visibleCausesCount = useSelector(getVisibleCausesCount);
  const prevVisibleUuidsRef = useRef(null);

  const handleOnCardClick = async (uuid, counteractions, clearSelected) => {
    if (clearSelected) {
      dispatch(cardClick({ cause: null }));
    } else {
      const fCause = causes.find((c) => c.uuid === uuid);
      // If this cause has a dot, mark it as seen
      if (fCause && fCause.showDot) {
        dispatch(markCauseSeen(uuid));
      }
      dispatch(cardClick({ cause: uuid }));
      await postCauseOrConsequenceClickedMetrics({
        session_id: sessionId,
        id: fCause.functionId,
        rank: fCause.rank,
        state: fCause.state,
        action_name: 'CAUSE_CLICKED',
        reason: 'MANUAL',
      });
    }
  };

  const clearSelected = useCallback(() => {
    dispatch(cardClick({ cause: null }));
  }, []);

  // useEffect(() => {
  //   return async () => {
  //     await postSessionClosedMetrics(sessionId, 'SESSION_CLOSED', null);
  //   };
  // }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (causes.length > 0) {
        if (!selected) {
          dispatch(cardClick({ cause: causes[0].uuid }));
          await postCauseOrConsequenceClickedMetrics({
            session_id: sessionId,
            id: causes[0].functionId,
            rank: causes[0].rank,
            state: causes[0].state,
            action_name: 'CAUSE_CLICKED',
            reason: 'AUTO',
          });
        }
      }
    };
    fetchData();
  }, [causes, selected]);

  // Track visibility transitions — dot causes entering/leaving visible area
  useEffect(() => {
    const currentVisibleUuids = new Set(causes.slice(0, visibleCausesCount).map((c) => c.uuid));

    // Seed the ref once we have data — skip empty renders and don't trigger dots
    if (prevVisibleUuidsRef.current === null) {
      if (causes.length > 0) {
        prevVisibleUuidsRef.current = currentVisibleUuids;
      }
      return;
    }

    const prevVisible = prevVisibleUuidsRef.current;

    // Causes entering visible area that haven't been seen — give them a dot
    const entering = [];
    currentVisibleUuids.forEach((uuid) => {
      if (!prevVisible.has(uuid) && !seenCauses[uuid]) {
        entering.push(uuid);
      }
    });
    if (entering.length > 0) {
      dispatch(addDotCauses(entering));
    }

    // Causes leaving visible area — reset their seen status
    const leaving = [];
    prevVisible.forEach((uuid) => {
      if (!currentVisibleUuids.has(uuid)) {
        leaving.push(uuid);
      }
    });
    if (leaving.length > 0) {
      dispatch(resetSeenCauses(leaving));
    }

    prevVisibleUuidsRef.current = currentVisibleUuids;
  }, [causes, visibleCausesCount, seenCauses, dispatch]);

  // Single fallback timer to clear expired dots if no WS update arrives
  useEffect(() => {
    const dotItems = causes.filter((c) => c.showDot && c.dotTimestamp);
    if (dotItems.length === 0) return;

    const timeoutMs = (parseInt(process.env.REACT_APP_NEW_RC_INDICATOR_SECONDS, 10) || 300) * 1000;
    const now = Date.now();
    const earliestExpiry = Math.min(...dotItems.map((c) => c.dotTimestamp + timeoutMs));
    const remaining = Math.max(0, earliestExpiry - now);

    const timer = setTimeout(() => {
      dispatch(clearExpiredDots());
    }, remaining);

    return () => clearTimeout(timer);
  }, [causes, dispatch]);

  return (
    <ConsequencesContainer>
      <ListHead>
        <IconTopBarLogo></IconTopBarLogo>
      </ListHead>
      <AnimateSharedLayout>
        <StyledCausesListContainer
          withCounteractions={selected && corrective && corrective.length > 0 ? true : false}
        >
          <StyledSelectedCausesLOL>
            {!helpPageOpen ? (
              <StyledHeading>Selected Cause</StyledHeading>
            ) : (
              <StyledHeading>&nbsp;</StyledHeading>
            )}
          </StyledSelectedCausesLOL>

          {selected ? <SelectedCause.Selected selected={selected} /> : <SelectedCause.BlankState />}
          {selected && COUNTERACTIONS && corrective && corrective.length > 0 && (
            <Counteraction counteractions={corrective} />
          )}
          <MainList list={causes} listName="cause" highlight={highlightedCauses}>
            {causes.map((item) => {
              const isSelected = item.uuid === selected?.uuid;
              return (
                <Card
                  type={'cause'}
                  key={item.uuid}
                  isSelected={isSelected}
                  data={item}
                  label={item.label}
                  id={item.uuid}
                  rankChange={item.rankChange}
                  showDot={item.showDot}
                  onCardClick={() => handleOnCardClick(item.uuid, isSelected)}
                  count={item.observationFunctions.length}
                  labels={item.state}
                  isFlagged={flaggedCauseUuids.has(item.uuid)}
                  flagComment={flaggedCauseUuids.get(item.uuid) || ''}
                  {...item}
                />
              );
            })}
          </MainList>
        </StyledCausesListContainer>
      </AnimateSharedLayout>
    </ConsequencesContainer>
  );
};

export default Causes;
